import { useEffect, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { usePageTitle } from '../hooks/usePageTitle';
import { TabMenu, type TabItem } from '@themes/aegix';
import { SMSPageHeader as PageHeader } from '../components/layout/SMSPageHeader';
import {
  HiMap,
  HiSquares2X2,
  HiShieldExclamation,
  HiClipboardDocumentList,
} from 'react-icons/hi2';
import {
  SitesFloorPlans,
  ResourcesList,
  ProtocolsList,
  SitesPlans,
} from '../components/sites';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFeature, useFeaturesLoading } from '../hooks/useFeature';
import { FeatureCode } from '../types/features';

const DEFAULT_TAB = 'floorplans';

interface SitesTab extends TabItem {
  subtitle: string;
}

const tabs: SitesTab[] = [
  {
    value: 'floorplans',
    label: 'Floor Plans',
    icon: HiMap,
    subtitle: 'Manage Sites, Buildings, Floors, Rooms, and Resources',
    'data-e2e': 'tab-floorplans',
  },
  {
    value: 'resources',
    label: 'Resources',
    icon: HiSquares2X2,
    subtitle: 'Manage Resources across Sites',
    'data-e2e': 'tab-resources',
  },
  {
    value: 'protocols',
    label: 'Protocols',
    icon: HiShieldExclamation,
    subtitle: 'Manage Alert Types and Protocols',
    'data-e2e': 'tab-protocols',
  },
  {
    value: 'plans',
    label: 'Plans',
    icon: HiClipboardDocumentList,
    subtitle: 'Drill scheduling, operating hours, routing, and terminology',
    'data-e2e': 'tab-plans',
  },
];

const tabContent: Record<string, React.FC> = {
  floorplans: SitesFloorPlans,
  resources: ResourcesList,
  protocols: ProtocolsList,
  plans: SitesPlans,
};

export function Sites() {
  const { t } = useTranslation();
  usePageTitle(t('sites.title', 'Sites'));
  const [searchParams, setSearchParams] = useSearchParams();
  const plansEnabled = useFeature(FeatureCode.SITE_PLANS);
  const featuresLoading = useFeaturesLoading();

  const tabEnabled = useMemo<Record<string, boolean>>(
    () => ({ plans: plansEnabled }),
    [plansEnabled],
  );

  const localizedTabs = useMemo(() => {
    return tabs.map((tab) => ({
      ...tab,
      label: t(`sites.tab.${tab.value}`, tab.label),
      subtitle: t(`sites.tabSubtitle.${tab.value}`, tab.subtitle),
    }));
  }, [t]);

  const visibleTabs = useMemo(
    () => localizedTabs.filter((tab) => tabEnabled[tab.value] !== false),
    [localizedTabs, tabEnabled],
  );

  const rawTab = searchParams.get('tab');

  // Flag-gated redirect: a tab whose flag is OFF falls back to the default
  // tab instead of rendering an empty page when deep-linked. Wait for the
  // feature query to settle so a user with the flag ON isn't bounced during
  // the initial load.
  useEffect(() => {
    if (featuresLoading) return;
    if (rawTab && tabEnabled[rawTab] === false) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', DEFAULT_TAB);
          next.delete('id');
          next.delete('public_id');
          next.delete('subtab');
          return next;
        },
        { replace: true },
      );
    }
  }, [featuresLoading, rawTab, tabEnabled, setSearchParams]);

  const activeTab = rawTab || DEFAULT_TAB;
  const isActiveTabEnabled = tabEnabled[activeTab] !== false;
  const renderedTab = isActiveTabEnabled ? activeTab : DEFAULT_TAB;
  const activeTabData = localizedTabs.find((t) => t.value === renderedTab);

  const handleTabChange = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      next.delete('id');
      next.delete('public_id');
      next.delete('subtab');
      return next;
    });
  };

  const ActiveContent = tabContent[renderedTab];

  return (
    <Box mx={-8} mt={-8}>
      <PageHeader
        icon={activeTabData?.icon as React.ElementType}
        title={activeTabData?.label || t('sites.title', 'Sites')}
        subtitle={activeTabData?.subtitle || t('sites.subtitle', 'Manage sites, buildings, floors, rooms, and operating plans.')}
        showSearch={true}
        showNotifications={true}
      />
      <TabMenu tabs={visibleTabs} activeTab={renderedTab} onChange={handleTabChange} e2eTitle="SitesTabs" />

      {ActiveContent && <ActiveContent />}
    </Box>
  );
}
